import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MapTouchGate from '../map/MapTouchGate';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@/utils/leafletInit';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import { Loader2, Plus, Trash2, ImageIcon, Star, Upload, Eye, EyeOff, Users, Lock, Map as MapIcon, List } from 'lucide-react';
import { useOrgTimezone, formatTimeInTz, formatLocalDateInTz } from '@/utils/timezone';
import cultivarService from '../../services/cultivar';
import orchardService from '../../services/orchard';
import OrgPicker from './OrgPicker';
import VisibilityConfigEditor from './VisibilityConfigEditor';
import EvaluationsPanel from './EvaluationsPanel';
import GrowingRegionPicker from './GrowingRegionPicker';
import { byCountryThenName } from './growingRegionDraft';
import type {
  AttributeDataType, AttributeDefinition, AttributeValue, EvaluatorGrant, GrowingRegion,
  CultivarMedia, CultivarMediaVisibility, VisibilityConfig,
} from '../../types/cultivar';
import type { Farm } from '../../types/orchard';

// ============================================================================
// MEDIA TAB
// ============================================================================

export function MediaTab({ cultivarId, isOwner }: { cultivarId: number; isOwner: boolean }) {
  const [media, setMedia] = useState<CultivarMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [caption, setCaption] = useState('');
  const [visibility, setVisibility] = useState<CultivarMediaVisibility>('licensed');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await cultivarService.listCultivarMedia(cultivarId);
      if (r.success && r.data) setMedia(r.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [cultivarId]);

  useEffect(() => { load(); }, [load]);

  const [blobUrls, setBlobUrls] = useState<Record<number, string>>({});
  useEffect(() => {
    let cancelled = false;
    const created: string[] = [];
    (async () => {
      const next: Record<number, string> = {};
      for (const m of media) {
        try {
          const url = await cultivarService.fetchCultivarMediaBlobUrl(m.id);
          if (cancelled) { URL.revokeObjectURL(url); continue; }
          next[m.id] = url;
          created.push(url);
        } catch {
          // ignore — tile will show broken image / link will be disabled
        }
      }
      if (!cancelled) setBlobUrls(next);
    })();
    return () => {
      cancelled = true;
      created.forEach(u => URL.revokeObjectURL(u));
    };
  }, [media]);

  const handleUpload = async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      await cultivarService.uploadCultivarMedia(cultivarId, file, {
        kind: file.type.startsWith('image/') ? 'photo' : 'document',
        caption: caption || undefined,
        visibility,
      });
      setCaption('');
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (e: any) {
      setError(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleVisibilityChange = async (id: number, v: CultivarMediaVisibility) => {
    try {
      await cultivarService.updateCultivarMedia(id, { visibility: v });
      await load();
    } catch (e: any) { window.alert(e?.message || 'Failed'); }
  };

  const handleSetPrimary = async (id: number) => {
    try {
      await cultivarService.updateCultivarMedia(id, { is_primary: true });
      await load();
    } catch (e: any) { window.alert(e?.message || 'Failed'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await cultivarService.deleteCultivarMedia(id);
      await load();
    } catch (e: any) { window.alert(e?.message || 'Failed'); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><ImageIcon className="h-5 w-5" />Media</CardTitle></CardHeader>
      <CardContent>
        {isOwner && (
          <div className="mb-4 p-3 border border-neutral-200 rounded-md bg-neutral-50">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-neutral-700 mb-1">Caption (optional)</label>
                <input className="w-full px-3 py-2 border border-neutral-300 rounded text-sm"
                       value={caption} onChange={e => setCaption(e.target.value)} placeholder="Polar Magic at colour break" />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Visibility</label>
                <select className="px-3 py-2 border border-neutral-300 rounded text-sm bg-white"
                        value={visibility} onChange={e => setVisibility(e.target.value as CultivarMediaVisibility)}>
                  <option value="public">public</option>
                  <option value="licensed">licensed</option>
                  <option value="owner">owner only</option>
                </select>
              </div>
              <div>
                <input ref={fileRef} type="file" className="hidden"
                       accept="image/*,application/pdf"
                       onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
                <Button variant="primary" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                  Upload
                </Button>
              </div>
            </div>
          </div>
        )}

        {error && <div className="mb-3 p-2 rounded bg-error-50 text-error-800 text-sm">{error}</div>}

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary-600" /></div>
        ) : media.length === 0 ? (
          <p className="text-sm text-neutral-500 py-6 text-center">No media yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {media.map(m => (
              <div key={m.id} className="border border-neutral-200 rounded-md overflow-hidden bg-white">
                {m.kind === 'photo' ? (
                  blobUrls[m.id] ? (
                    <img src={blobUrls[m.id]} alt={m.caption || m.file_name}
                         className="w-full h-40 object-cover bg-neutral-100" />
                  ) : (
                    <div className="w-full h-40 flex items-center justify-center bg-neutral-100">
                      <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
                    </div>
                  )
                ) : (
                  <div className="w-full h-40 flex items-center justify-center bg-neutral-100 text-neutral-400">
                    {blobUrls[m.id] ? (
                      <a href={blobUrls[m.id]} target="_blank" rel="noreferrer"
                         className="text-primary-600 hover:underline text-sm">
                        Open {m.file_name}
                      </a>
                    ) : (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    )}
                  </div>
                )}
                <div className="p-2 text-sm">
                  {m.caption && <p className="text-neutral-800 line-clamp-2">{m.caption}</p>}
                  <div className="mt-1 flex items-center justify-between">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      m.visibility === 'public' ? 'bg-emerald-100 text-emerald-800'
                      : m.visibility === 'licensed' ? 'bg-blue-100 text-blue-800'
                      : 'bg-neutral-200 text-neutral-700'
                    }`}>{m.visibility}</span>
                    {m.is_primary && <span className="text-xs text-amber-700 inline-flex items-center gap-0.5">
                      <Star className="h-3 w-3 fill-current" /> primary
                    </span>}
                  </div>
                  {isOwner && (
                    <div className="mt-2 flex items-center gap-1">
                      <select className="text-xs px-1 py-0.5 border border-neutral-300 rounded bg-white"
                              value={m.visibility}
                              onChange={e => handleVisibilityChange(m.id, e.target.value as CultivarMediaVisibility)}>
                        <option value="public">public</option>
                        <option value="licensed">licensed</option>
                        <option value="owner">owner</option>
                      </select>
                      {!m.is_primary && (
                        <button className="text-xs text-amber-700 hover:underline" onClick={() => handleSetPrimary(m.id)}>
                          Set primary
                        </button>
                      )}
                      <button className="ml-auto text-neutral-400 hover:text-error-600" onClick={() => handleDelete(m.id)}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// OBSERVATIONS / ATTRIBUTE VALUES TAB
// ============================================================================

export function AttributeValuesTab({ cultivarId, cultivarName = 'this cultivar' }: { cultivarId: number; cultivarName?: string }) {
  const [defs, setDefs] = useState<AttributeDefinition[]>([]);
  const [values, setValues] = useState<AttributeValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const orgTz = useOrgTimezone();
  // Where the reading was taken. A farm is how a single observation reaches a
  // growing region: the maturity roll-up resolves one through
  // COALESCE(evaluation.growing_region_id, farm.growing_region_id), and this
  // form writes no evaluation. Without a farm the reading is stored and then
  // never counted.
  const [farms, setFarms] = useState<Farm[]>([]);
  const [farmsUnavailable, setFarmsUnavailable] = useState(false);
  const [regions, setRegions] = useState<GrowingRegion[]>([]);
  const [form, setForm] = useState({
    attribute_id: 0,
    value_str: '',
    season_year: new Date().getFullYear(),
    farm_id: 0,
    growing_region_id: 0,
    source_notes: '',
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [d, v, g] = await Promise.all([
          cultivarService.listAttributeDefinitions(),
          cultivarService.listAttributeValues(cultivarId),
          cultivarService.listGrowingRegions(),
        ]);
        if (d.success && d.data) setDefs(d.data);
        if (v.success && v.data) setValues(v.data);
        if (g.success && g.data) setRegions([...g.data].sort(byCountryThenName));
      } catch (e: any) {
        setError(e?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }

      // Farms come from the orchard module, which an org recording observations
      // may not hold a seat for. A failure here leaves the field explaining
      // itself rather than breaking the observations tab.
      try {
        const f = await orchardService.getAllFarms();
        if (f.success && f.data) setFarms(f.data.farms);
        else setFarmsUnavailable(true);
      } catch {
        setFarmsUnavailable(true);
      }
    };
    load();
  }, [cultivarId]);

  const selectedDef = defs.find(d => d.id === form.attribute_id);
  const selectedFarm = farms.find(f => f.id === form.farm_id);

  // Where the roll-up will actually look: the region named on the reading, then
  // the farm's. Same precedence as deriveObservedRecords, so this warning can
  // never disagree with what gets counted.
  const resolvedRegionId = form.growing_region_id || selectedFarm?.growing_region_id || null;

  // A maturity date is the one reading this form can silently waste: it saves
  // fine and is then skipped by the roll-up, which requires a region. A nudge,
  // not a block — same as EvaluationForm's — because the reading is still worth
  // having.
  const maturityWithoutRegion =
    selectedDef?.slug === 'maturity_date' &&
    form.value_str !== '' &&
    resolvedRegionId == null;

  const parseValue = (def: AttributeDefinition, str: string): unknown => {
    switch (def.data_type) {
      case 'number': return Number(str);
      case 'boolean': return str === 'true';
      case 'date': return str;
      case 'enum': return str;
      case 'range': {
        const [a, b] = str.split('-').map(s => Number(s.trim()));
        return { min: a, max: b };
      }
      default: return str;
    }
  };

  const handleSubmit = async () => {
    if (!form.attribute_id || !selectedDef) { setError('Pick an attribute'); return; }
    if (form.value_str === '' && selectedDef.data_type !== 'boolean') { setError('Value required'); return; }
    setError(null);
    setSubmitting(true);
    try {
      const value = parseValue(selectedDef, form.value_str);
      await cultivarService.createAttributeValue(cultivarId, {
        attribute_id: form.attribute_id,
        value,
        season_year: selectedDef.is_seasonal ? form.season_year : null,
        // The server validates the farm against the submitting org and fills
        // latitude/longitude from its centroid, which is what puts the reading
        // on the map view. No coordinates are copied down here.
        farm_id: form.farm_id || null,
        growing_region_id: form.growing_region_id || null,
        source_notes: form.source_notes || null,
      });
      setShowForm(false);
      setForm({
        attribute_id: 0, value_str: '', season_year: new Date().getFullYear(),
        farm_id: 0, growing_region_id: 0, source_notes: '',
      });
      const v = await cultivarService.listAttributeValues(cultivarId);
      if (v.success && v.data) setValues(v.data);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this observation?')) return;
    try {
      await cultivarService.deleteAttributeValue(id);
      setValues(values.filter(v => v.id !== id));
    } catch (e: any) { window.alert(e?.message || 'Failed'); }
  };

  const grouped = useMemo(() => {
    const m = new Map<string, AttributeValue[]>();
    for (const v of values) {
      const k = v.attribute_category || 'other';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(v);
    }
    return Array.from(m.entries()).sort();
  }, [values]);

  return (
    <div className="space-y-6">
    {/* Full field evaluations — many readings, photos and follow-ups per visit. */}
    <EvaluationsPanel cultivarId={cultivarId} cultivarName={cultivarName} />

    {/* The original single-reading form. Kept for quick desk-bound corrections
        and for viewing observations recorded before evaluations existed. */}
    <Card>
      <CardHeader>
        {/* Title, view toggle and the add button do not fit on one line at
            phone width — they wrap rather than run off the right edge. */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="flex items-center gap-2"><Eye className="h-5 w-5" />Individual observations</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border border-neutral-300 bg-white overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`px-2 py-1 text-xs inline-flex items-center gap-1 ${viewMode === 'list' ? 'bg-primary-50 text-primary-700' : 'text-neutral-600 hover:bg-neutral-50'}`}
              >
                <List className="h-3.5 w-3.5" /> List
              </button>
              <button
                type="button"
                onClick={() => setViewMode('map')}
                className={`px-2 py-1 text-xs inline-flex items-center gap-1 border-l border-neutral-300 ${viewMode === 'map' ? 'bg-primary-50 text-primary-700' : 'text-neutral-600 hover:bg-neutral-50'}`}
              >
                <MapIcon className="h-3.5 w-3.5" /> Map
              </button>
            </div>
            <Button variant="primary" onClick={() => setShowForm(s => !s)}>
              <Plus className="h-4 w-4 mr-1" /> {showForm ? 'Cancel' : 'Add observation'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && <div className="mb-3 p-2 rounded bg-error-50 text-error-800 text-sm">{error}</div>}

        {showForm && (
          <div className="mb-4 p-3 border border-neutral-200 rounded-md bg-neutral-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Attribute</label>
                <select className="w-full px-3 py-2 border border-neutral-300 rounded text-sm bg-white"
                        value={form.attribute_id}
                        onChange={e => setForm({ ...form, attribute_id: Number(e.target.value), value_str: '' })}>
                  <option value={0}>— select —</option>
                  {defs.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.display_name} ({d.category}{d.unit ? ` · ${d.unit}` : ''})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  Value{selectedDef?.unit ? ` (${selectedDef.unit})` : ''}
                </label>
                {selectedDef?.data_type === 'enum' ? (
                  <select className="w-full px-3 py-2 border border-neutral-300 rounded text-sm bg-white"
                          value={form.value_str} onChange={e => setForm({ ...form, value_str: e.target.value })}>
                    <option value="">— select —</option>
                    {(selectedDef.enum_options || []).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : selectedDef?.data_type === 'boolean' ? (
                  <select className="w-full px-3 py-2 border border-neutral-300 rounded text-sm bg-white"
                          value={form.value_str} onChange={e => setForm({ ...form, value_str: e.target.value })}>
                    <option value="">— select —</option>
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                ) : (
                  <input className="w-full px-3 py-2 border border-neutral-300 rounded text-sm"
                         type={selectedDef?.data_type === 'date' ? 'date'
                              : selectedDef?.data_type === 'number' ? 'number' : 'text'}
                         value={form.value_str}
                         onChange={e => setForm({ ...form, value_str: e.target.value })}
                         placeholder={selectedDef?.data_type === 'range' ? '10 - 15' : ''} />
                )}
              </div>
              {selectedDef?.is_seasonal && (
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Season year</label>
                  <input type="number"
                         className="w-full px-3 py-2 border border-neutral-300 rounded text-sm"
                         value={form.season_year}
                         onChange={e => setForm({ ...form, season_year: Number(e.target.value) })} />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Farm (optional)</label>
                <select className="w-full px-3 py-2 border border-neutral-300 rounded text-sm bg-white"
                        value={form.farm_id}
                        disabled={farmsUnavailable || farms.length === 0}
                        onChange={e => setForm({ ...form, farm_id: Number(e.target.value) })}>
                  <option value={0}>— not recorded —</option>
                  {farms.map(f => (
                    <option key={f.id} value={f.id}>
                      {f.name}{f.growing_region_id == null ? ' (no growing region)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-neutral-500 mt-1">
                  {farmsUnavailable
                    ? 'Farms could not be loaded. Name the growing region below instead.'
                    : farms.length === 0
                      ? 'No farms recorded yet. Name the growing region below instead.'
                      : 'Puts the reading on the map, and supplies its growing region when you leave that blank.'}
                </p>
              </div>
              {/* The region is what the maturity roll-up needs, and it is asked
                  for directly rather than only through a farm: an org that has
                  not mapped its orchard has no farm to inherit one from, and
                  the picker can create a region without leaving the form. */}
              <div className="md:col-span-2">
                <GrowingRegionPicker
                  regions={regions}
                  value={form.growing_region_id}
                  onChange={(id) => setForm({ ...form, growing_region_id: id })}
                  onCreated={(r) => {
                    setRegions(prev => [...prev, r].sort(byCountryThenName));
                    setForm(f => ({ ...f, growing_region_id: r.id }));
                  }}
                  label="Growing region"
                />
                <p className="text-[11px] text-neutral-500 mt-1">
                  {form.growing_region_id === 0 && selectedFarm?.growing_region_id != null
                    ? `Leave blank to use ${selectedFarm.name}'s region, which is what will be counted.`
                    : 'The climate area this reading was taken in — what maturity dates are averaged by. Not a state or a licensing territory.'}
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-neutral-700 mb-1">Notes (optional)</label>
                <input className="w-full px-3 py-2 border border-neutral-300 rounded text-sm"
                       value={form.source_notes}
                       onChange={e => setForm({ ...form, source_notes: e.target.value })} />
              </div>
            </div>

            {maturityWithoutRegion && (
              <div className="mt-3 rounded-lg bg-warning-50 border border-warning-200 px-3 py-2 text-xs text-warning-800">
                {selectedFarm
                  ? <>{selectedFarm.name} has no growing region, so this maturity date won't be counted towards the cultivar's harvest timing. Pick a region above, or set one on the farm.</>
                  : <>You've recorded a maturity date but no growing region, so it won't be counted towards this cultivar's harvest timing. Pick one above — you can create it there if it isn't listed.</>}
              </div>
            )}

            <div className="flex justify-end mt-3">
              <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary-600" /></div>
        ) : values.length === 0 ? (
          <p className="text-sm text-neutral-500 py-6 text-center">No observations yet.</p>
        ) : viewMode === 'map' ? (
          <ObservationsMap values={values} />
        ) : (
          <div className="space-y-4">
            {grouped.map(([cat, rows]) => (
              <div key={cat}>
                <h4 className="text-sm font-medium text-neutral-700 capitalize mb-2">{cat.replace(/_/g, ' ')}</h4>
                <div className="divide-y divide-neutral-200 border border-neutral-200 rounded-md">
                  {rows.map(v => (
                    <div key={v.id} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
                      <div className="flex-1">
                        <span className="font-medium">{v.attribute_display_name}</span>
                        <span className="ml-2 text-neutral-800">{formatValue(v.value, v.attribute_data_type, orgTz)}</span>
                        {v.attribute_unit && <span className="text-neutral-500 ml-1">{v.attribute_unit}</span>}
                        {v.season_year && <span className="text-neutral-500 ml-2">· {v.season_year}</span>}
                        {/* Where it was taken. The region is what decides
                            whether a maturity date reaches the roll-up at all,
                            so it is stated even when there is no farm. */}
                        <span className="text-neutral-500 ml-2">
                          · {v.growing_region_name
                              || regions.find(r => r.id === v.growing_region_id)?.name
                              || 'no growing region'}
                        </span>
                        {(v.farm_name || v.farm_id != null) && (
                          <span className="text-neutral-500 ml-2">
                            · {v.farm_name || farms.find(f => f.id === v.farm_id)?.name}
                          </span>
                        )}
                        {v.source_notes && <span className="text-neutral-500 ml-2 italic">— {v.source_notes}</span>}
                      </div>
                      <div className="text-xs text-neutral-500 text-right">
                        <div>{v.observer_org_name || '—'}</div>
                        <div className="capitalize">{v.observer_role}</div>
                      </div>
                      <button className="text-neutral-400 hover:text-error-600 ml-1" onClick={() => handleDelete(v.id)}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
    </div>
  );
}

/**
 * An attribute value is stored as JSON and could be anything the definition
 * says, so the definition's data_type decides how it reads.
 *
 * A date was the one being printed raw: a maturity date showed as
 * "2024-11-07", which is a storage format, not a date anybody writes down.
 * The shape check is a fallback for rows whose definition did not come back
 * with the value — a bare YYYY-MM-DD is a date by any reading.
 */
function formatValue(v: unknown, dataType?: AttributeDataType, timezone?: string): string {
  if (v == null) return '—';
  if (typeof v === 'object' && 'min' in (v as any) && 'max' in (v as any)) {
    return `${(v as any).min}–${(v as any).max}`;
  }
  const str = String(v);
  const looksLikeDate = /^\d{4}-\d{2}-\d{2}$/.test(str);
  if (timezone && looksLikeDate && (dataType === 'date' || dataType === undefined)) {
    // A DATE column, not a timestamp — formatLocalDateInTz anchors at noon so
    // the calendar day survives any org timezone.
    return formatLocalDateInTz(str, timezone);
  }
  return str;
}

// ============================================================================
// EVALUATOR GRANTS TAB
// ============================================================================

export function EvaluatorsTab({ cultivarId }: { cultivarId: number }) {
  const [grants, setGrants] = useState<EvaluatorGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<{ id: number; name: string } | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await cultivarService.listEvaluatorGrants({ cultivar_id: cultivarId });
      if (r.success && r.data) setGrants(r.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [cultivarId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!selectedOrg) { setError('Pick an evaluator organisation'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await cultivarService.createEvaluatorGrant({
        cultivar_id: cultivarId, evaluator_org_id: selectedOrg.id,
        notes: notes || null,
      });
      setSelectedOrg(null); setNotes('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (id: number) => {
    if (!window.confirm('Revoke this evaluator grant?')) return;
    try {
      await cultivarService.revokeEvaluatorGrant(id);
      await load();
    } catch (e: any) { window.alert(e?.message || 'Failed'); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" />Evaluator grants</CardTitle></CardHeader>
      <CardContent>
        <div className="mb-4 p-3 border border-neutral-200 rounded-md bg-neutral-50">
          <p className="text-xs text-neutral-600 mb-2">
            Grant another organisation the right to record observations against this cultivar.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[260px]">
              <label className="block text-xs font-medium text-neutral-700 mb-1">Evaluator organisation</label>
              <OrgPicker value={selectedOrg} onChange={setSelectedOrg} />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-neutral-700 mb-1">Notes (optional)</label>
              <input className="w-full px-3 py-2 border border-neutral-300 rounded text-sm"
                     value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
            <Button variant="primary" onClick={handleAdd} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Add
            </Button>
          </div>
        </div>

        {error && <div className="mb-3 p-2 rounded bg-error-50 text-error-800 text-sm">{error}</div>}

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary-600" /></div>
        ) : grants.length === 0 ? (
          <p className="text-sm text-neutral-500 py-6 text-center">No evaluator grants yet.</p>
        ) : (
          <div className="divide-y divide-neutral-200 border border-neutral-200 rounded-md">
            {grants.map(g => (
              <div key={g.id} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
                <div>
                  <div className="font-medium">{g.evaluator_org_name || `Org #${g.evaluator_org_id}`}</div>
                  {g.notes && <div className="text-xs text-neutral-500">{g.notes}</div>}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded ${g.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-neutral-200 text-neutral-600'}`}>
                    {g.status}
                  </span>
                  {g.status === 'active' && (
                    <button className="text-neutral-400 hover:text-error-600" onClick={() => handleRevoke(g.id)}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// VISIBILITY CONFIG TAB
// ============================================================================

export function VisibilityTab({ cultivarId, initial }: { cultivarId: number; initial: VisibilityConfig }) {
  const [config, setConfig] = useState<VisibilityConfig>(initial || {});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const orgTz = useOrgTimezone();

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await cultivarService.updateCultivarVisibilityConfig(cultivarId, config);
      setSavedAt(formatTimeInTz(new Date().toISOString(), orgTz));
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5" />Per-audience visibility</CardTitle></CardHeader>
      <CardContent>
        {error && <div className="mb-3 p-2 rounded bg-error-50 text-error-800 text-sm">{error}</div>}
        <VisibilityConfigEditor
          value={config}
          onChange={setConfig}
          intro="Extend the system defaults: tick extra fields or attribute categories you want each audience to see. Owner and licensors always see everything; settings here cannot remove the minimum (name, trade name, crop type, program)."
        />
        <div className="mt-4 flex items-center justify-end gap-3">
          {savedAt && <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
            <EyeOff className="h-3 w-3" /> Saved at {savedAt}
          </span>}
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save visibility
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// OBSERVATIONS MAP
// ============================================================================
// Groups observation values by lat/lng pin (rounded to ~5dp) so multiple
// attributes recorded at the same location surface in one popup. Pins are
// colour-coded by observer_role.

const ROLE_COLOURS: Record<string, string> = {
  owner: '#16A34A',     // emerald
  evaluator: '#7C3AED', // violet
  grower: '#0284C7',    // sky blue
};

function makeDotIcon(colour: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${colour};border:2px solid white;box-shadow:0 0 0 1px rgba(0,0,0,0.3);"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

function ObservationsMap({ values }: { values: AttributeValue[] }) {
  const orgTz = useOrgTimezone();
  const points = useMemo(() => {
    return values.filter(
      v => typeof v.latitude === 'number' && typeof v.longitude === 'number'
    );
  }, [values]);

  // Group by rounded coordinate so co-located observations share a pin.
  const groups = useMemo(() => {
    const m = new Map<string, { lat: number; lng: number; rows: AttributeValue[] }>();
    for (const p of points) {
      const lat = Number(p.latitude!.toFixed(5));
      const lng = Number(p.longitude!.toFixed(5));
      const key = `${lat},${lng}`;
      if (!m.has(key)) m.set(key, { lat, lng, rows: [] });
      m.get(key)!.rows.push(p);
    }
    return Array.from(m.values());
  }, [points]);

  if (points.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-neutral-500">
        None of the observations have a recorded location yet. Add a farm or lat/lng
        when capturing observations to see them here.
      </div>
    );
  }

  const centerLat = points.reduce((s, p) => s + (p.latitude || 0), 0) / points.length;
  const centerLng = points.reduce((s, p) => s + (p.longitude || 0), 0) / points.length;

  return (
    <div className="h-[420px] rounded-md overflow-hidden border border-neutral-200">
      <MapContainer center={[centerLat, centerLng]} zoom={6} className="h-full w-full">
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {groups.map(g => {
          // Pick dominant role at this point for the marker colour.
          const role = g.rows[0].observer_role;
          const colour = ROLE_COLOURS[role] || '#525252';
          return (
            <Marker key={`${g.lat},${g.lng}`} position={[g.lat, g.lng]} icon={makeDotIcon(colour)}>
              <Popup>
                <div className="text-xs space-y-1 min-w-[200px]">
                  {g.rows[0].farm_name && (
                    <div className="font-semibold">{g.rows[0].farm_name}</div>
                  )}
                  {g.rows.map(r => (
                    <div key={r.id} className="border-t border-neutral-100 pt-1 first:border-0 first:pt-0">
                      <div className="font-medium">{r.attribute_display_name}: {formatValue(r.value, r.attribute_data_type, orgTz)}{r.attribute_unit ? ` ${r.attribute_unit}` : ''}</div>
                      <div className="text-neutral-500">
                        {r.observer_org_name || `Org #${r.observed_by_org_id}`} · {r.observer_role}
                        {r.season_year ? ` · ${r.season_year}` : ''}
                      </div>
                      {r.source_notes && <div className="text-neutral-500 italic">{r.source_notes}</div>}
                    </div>
                  ))}
                </div>
              </Popup>
            </Marker>
          );
        })}
        <MapTouchGate />
      </MapContainer>
    </div>
  );
}
